import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  IconButton,
  Divider,
} from "@mui/material";

import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import logo from "../../assets/logo.png";

const serviceItems = [
  {
    name: "SAP Consulting & Implementation",
    path: "/services/sap-consulting-implementation",
  },
  {
    name: "SAP Support & Managed Services",
    path: "/services/sap-support-managed-services",
  },
  {
    name: "SAP Upgrade & Migration Services",
    path: "/services/sap-upgrade-migration-services",
  },
  {
    name: "Digital Transformation",
    path: "/services/digital-transformation",
  },
  {
    name: "Cloud Services",
    path: "/services/cloud-services",
  },
  {
    name: "Custom Application Development",
    path: "/services/custom-application-development",
  },
  {
    name: "Data Analytics & Business Intelligence",
    path: "/services/data-analytics-business-intelligence",
  },
  {
    name: "AI, IoT & Intelligent Automation",
    path: "/services/ai-iot-intelligent-automation",
  },
];

const companyItems = [
  { name: "About Us", path: "/about" },
  { name: "Contact Us", path: "/contact-us" },
  {
    name: "Services",
    path: "/",
    scrollId: "capabilities-section",
  },
];

const socialItems = [
  {
    icon: LinkedInIcon,
    label: "LinkedIn",
    path: "https://www.linkedin.com/company/zencon-limited-co/",
  },
];

const Footer = () => {
  const navigate = useNavigate();

  const handleFooterNavigation = (item) => {
    if (item.scrollId) {
      navigate(item.path);

      setTimeout(() => {
        const section = document.getElementById(item.scrollId);

        section?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);

      return;
    }

    navigate(item.path);
  };

  return (
    <Box
      component="footer"
      sx={{
        background:
          "linear-gradient(135deg, #07111f 0%, #0f2537 55%, #123524 100%)",
        color: "#fff",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, sm: 3 } }}
      >
        <Grid container spacing={{ xs: 5, md: 6 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={3}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  component="img"
                  src={logo}
                  alt="ZENCON"
                  sx={{ height: 40 }}
                />
              </Box>

              <Typography
                sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.8 }}
              >
                Smarter SAP solutions designed to help businesses simplify,
                optimize, and scale their operations with confidence.
              </Typography>

              <Stack spacing={1.5}>
                <ContactItem icon={<PhoneIcon />} text="+966 57 526 8281" />
                <ContactItem icon={<EmailIcon />} text="info@zencon-sa.com" />
                <ContactItem icon={<LocationOnIcon />} text="Saudi Arabia" />
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 7, md: 5 }}>
            <FooterTitle title="Services" />

            <Grid container spacing={1}>
              {serviceItems.map((item) => (
                <Grid size={{ xs: 12, sm: 6 }} key={item.path}>
                  <FooterLink label={item.name} path={item.path} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, sm: 5, md: 3 }}>
            <FooterTitle title="Company" />

            <Stack spacing={1.2}>
              {companyItems.map((item) => (
                <FooterButtonLink
                  key={item.name}
                  label={item.name}
                  onClick={() => handleFooterNavigation(item)}
                />
              ))}
            </Stack>

            <Stack direction="row" spacing={1.2} sx={{ mt: 4 }}>
              {socialItems.map(({ icon: Icon, label, path }) => (
                <IconButton
                  key={label}
                  component="a"
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  sx={{
                    width: 42,
                    height: 42,
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.06)",
                    transition: "0.3s",
                    "&:hover": {
                      background: "#22c55e",
                      color: "#07111f",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5, borderColor: "rgba(255,255,255,0.12)" }} />

        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: 14 }}>
            © 2026 Zencon. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

const FooterTitle = ({ title }) => (
  <Typography
    variant="h6"
    sx={{
      mb: 2.5,
      fontWeight: 700,
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        left: 0,
        bottom: -8,
        width: 38,
        height: 3,
        borderRadius: 10,
        background: "#22c55e",
      },
    }}
  >
    {title}
  </Typography>
);

const linkStyles = {
  color: "rgba(255,255,255,0.72)",
  fontSize: "0.94rem",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.8,
  textDecoration: "none",
  transition: "0.3s",
  cursor: "pointer",
  background: "transparent",
  border: "none",
  p: 0,
  fontFamily: "inherit",
  textAlign: "left",
  "&:hover": {
    color: "#22c55e",
    transform: "translateX(6px)",
  },
};

const FooterLink = ({ label, path }) => (
  <Box component={RouterLink} to={path} sx={linkStyles}>
    <ArrowForwardIosIcon sx={{ fontSize: 10 }} />
    {label}
  </Box>
);

const FooterButtonLink = ({ label, onClick }) => (
  <Box component="button" onClick={onClick} sx={linkStyles}>
    <ArrowForwardIosIcon sx={{ fontSize: 10 }} />
    {label}
  </Box>
);

const ContactItem = ({ icon, text }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        color: "#22c55e",
        background: "rgba(34,197,94,0.12)",
      }}
    >
      {icon}
    </Box>

    <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 15 }}>
      {text}
    </Typography>
  </Box>
);

export default Footer;
